import React from 'react';
import { motion } from 'framer-motion';
import { Award, Heart, Key, Users } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We maintain the highest standards in every detail, from marble finishes to 24-hour power supply.'
    },
    {
      icon: Heart,
      title: 'Comfort',
      description: 'Your comfort is our priority. Every suite is designed to feel like your own private residence.'
    },
    {
      icon: Key,
      title: 'Privacy',
      description: 'Enjoy the independence of apartment living with the security and service of a premium hotel.'
    },
    {
      icon: Users,
      title: 'Hospitality',
      description: 'Our team is dedicated to ensuring your stay exceeds expectations in every way.'
    }
  ];

  return (
    <div data-testid="about-page" className="min-h-screen bg-[#FAF8F5] pt-24">
      {/* Hero Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1
              data-testid="about-page-title"
              className="font-serif font-bold tracking-tighter leading-none text-[#1E3A8A] mb-6"
              style={{ fontSize: '26px' }}
            >
              Built for Those Who Know the Difference.
            </h1>
            <p className="text-base leading-relaxed text-[#3A3A3A] mb-6">
              De Azraa Luxury Apartments was created for guests who expect more than a room — they expect an experience. Located in the prestigious Udoka Housing Estate, Awka, each suite combines the privacy of a personal residence with the standards of five-star hospitality.
            </p>
            <p className="text-base leading-relaxed text-[#3A3A3A] mb-6">
              From the moment you step through our doors, you'll notice the difference. The warm gold LED lighting that frames each ceiling. The cool elegance of marble beneath your feet. The quiet hum of 24-hour electricity that most Nigerian hotels only promise. The certainty that your valuables are secure, your internet is fast, and your kitchen is fully equipped.
            </p>
            <p className="text-base leading-relaxed text-[#3A3A3A] mb-8">
              We didn't build De Azraa for everyone. We built it for you — the guest who notices when excellence is absent, and appreciates when it's present.
            </p>
            <p className="font-serif text-2xl text-[#C9A84C] italic" data-testid="about-tagline">
              Pure Comfort. Timeless Luxury.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative h-[600px]"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_0902ddbc-a622-478c-8995-4613e012561c/artifacts/bi2ck6w2_image.png"
              alt="De Azraa Building Exterior"
              data-testid="about-building-image"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#F2EBE1]">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2
            data-testid="about-values-title"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1A1A1A] mb-4"
          >
            Our Values
          </h2>
          <p className="text-base leading-relaxed text-[#3A3A3A] max-w-3xl mx-auto">
            Everything we do is guided by these principles.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              data-testid={`about-value-${index}`}
              className="text-center p-8"
            >
              <value.icon
                className="text-[#C9A84C] mx-auto mb-4"
                size={36}
                strokeWidth={1}
              />
              <h3 className="text-xl font-medium text-[#1A1A1A] mb-3">{value.title}</h3>
              <p className="text-base leading-relaxed text-[#3A3A3A]">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2
            data-testid="about-location-title"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1A1A1A] mb-4"
          >
            Find Us in Awka
          </h2>
          <p className="text-base leading-relaxed text-[#3A3A3A] max-w-3xl mx-auto">
            Strategically located in Udoka Housing Estate Phase 1, one of Awka's most prestigious residential areas.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-white border border-[#E5E5E5] p-8 max-w-2xl mx-auto text-center"
        >
          <p className="text-base leading-relaxed text-[#3A3A3A] mb-6" data-testid="about-address">
            Plot 10/9 Udoka Housing Estate Phase 1<br />
            Awka, Anambra State, Nigeria
          </p>
          <a
            href="tel:+2348066722221"
            data-testid="about-phone-link"
            className="text-[#C9A84C] hover:text-[#1A1A1A] transition-colors text-lg font-medium"
          >
            +234 806 672 2221
          </a>
        </motion.div>
      </section>
    </div>
  );
};

export default About;